import React, { Component, MouseEvent } from 'react';
import CustomTable from '../../custom/CustomTable';
import { User, UserState } from '../../../domain/rbac/RbacDomains';
import { ColumnProps } from 'primereact/column';
import Icon from '../../../icons/Icon';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { RootState } from '../../../redux/store';
import { addPagePath, changePagePath } from '../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import NavigationUtil from '../../../util/NavigationUtil';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment/moment';
import MakerCheckerClient from '../../../client/maker-checker/MakerCheckerClient';
import GatewayContainer from '../../../config/GatewayContainer';
import GatewayNames from '../../../constant/GatewayNames';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class UserListComponent extends Component<Props> {
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly makerCheckerClient: MakerCheckerClient = GatewayContainer.getInstance().get(GatewayNames.MAKER_CHECKER_CLIENT);

  public render() {
    const { users, selectedUser } = this.props;
    return (
      <CustomTable
        selectionMode={'single'}
        dataKey='username'
        selection={selectedUser}
        onRowClick={this.openUpdatePage}
        columns={this.getColumns()}
        value={users}
      />
    );
  }

  private openUpdatePage = (e: DataTableRowClickEventParams) => {
    const { setSelectedUser, openEditor, setUpdateRequestData } = this.props;
    setSelectedUser(e.data);
    openEditor();
    this.makerCheckerClient.findByDocumentId(ComponentName.ADMIN_USER, e.data.username).then(setUpdateRequestData);
  };

  private getColumns = (): ColumnProps[] => {
    const columns = [
      {
        field: 'username',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.username', defaultMessage: 'Username' }),
        alignHeader: 'left',
        columnKey: 'username',
        className: 'min-w-[12%]',
        body: (record: User) => {
          return (
            <div className={'flex items-center w-full'}>
              <CustomTableContentWrapper showEllipsis={true} clipSize={15} text={record.username} data-testid={`${ComponentName.ADMIN_USER}_username_text`} />
            </div>
          );
        },
      },
      {
        field: 'firstName',
        columnKey: 'firstName',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.firstName', defaultMessage: 'First Name' }),
        className: 'min-w-[9%]',
        body: (record: User) => {
          return (
            <div className={'flex items-center w-full'}>
              <CustomTableContentWrapper clipSize={12} showEllipsis={true} text={record.firstName} data-testid={`${ComponentName.ADMIN_USER}_firstName_text`} />
            </div>
          );
        },
      },
      {
        field: 'lastName',
        columnKey: 'lastName',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.lastName', defaultMessage: 'Last Name' }),
        className: 'min-w-[9%]',
        body: (record: User) => {
          return (
            <div className={'flex items-center w-full'}>
              <CustomTableContentWrapper clipSize={12} showEllipsis={true} text={record.lastName} data-testid={`${ComponentName.ADMIN_USER}_lastName_text`} />
            </div>
          );
        },
      },
      {
        field: 'email',
        columnKey: 'email',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.email', defaultMessage: 'E-mail' }),
        className: 'min-w-[14%]',
        body: (record: User) => {
          return (
            <div className={'flex items-center w-full'}>
              <CustomTableContentWrapper clipSize={25} showEllipsis={true} text={record.email} data-testid={`${ComponentName.ADMIN_USER}_email_text`} />
            </div>
          );
        },
      },
      {
        field: 'roles',
        columnKey: 'roles',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.roles', defaultMessage: 'Roles' }),
        className: 'min-w-[13%]',
        body: (record: User) => {
          const roles = record.roles;
          const hasRoles = Array.isArray(roles) && roles.length > 0;
          const remainingRoleCount = hasRoles && roles.length > 2 ? roles.length - 2 : 0;

          return (
            <div className={'flex items-center w-full gap-x-2'} data-testid={`${ComponentName.ADMIN_USER}_roles_text`}>
              {hasRoles &&
                roles.map((role, index) => {
                  return (
                    index < 2 && (
                      <div key={index} className={'flex gap-x-2'}>
                        <CustomTableContentWrapper clipSize={8} text={role.toString()} />
                      </div>
                    )
                  );
                })}
              <div className={'text-xs 2xl:text-base'}>{remainingRoleCount > 0 && <p>+ {remainingRoleCount}</p>}</div>
            </div>
          );
        },
      },
      {
        field: 'groups',
        columnKey: 'groups',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.groups', defaultMessage: 'Groups' }),
        className: 'min-w-[9%]',
        body: (record: User) => {
          const groups = record.groups;
          const hasGroups = Array.isArray(groups) && groups.length > 0;
          const remainingGroupCount = hasGroups && groups.length > 2 ? groups.length - 2 : 0;

          return (
            <div className={'flex items-center w-full gap-x-2'} data-testid={`${ComponentName.ADMIN_USER}_groups_text`}>
              {hasGroups &&
                groups.map((group, index) => {
                  return (
                    index < 2 && (
                      <div key={index} className={'flex gap-x-2'}>
                        <CustomTableContentWrapper clipSize={7} text={group} />
                      </div>
                    )
                  );
                })}
              <div className={'text-xs 2xl:text-base'}>{remainingGroupCount > 0 && <p>+ {remainingGroupCount}</p>}</div>
            </div>
          );
        },
      },
      {
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.userType', defaultMessage: 'User Type' }),
        columnKey: 'activeRiskType',
        className: 'min-w-[8%]',
        field: 'activeRiskType',
        body: (record: User) => {
          const riskType = record.riskType == 'fraud' ? 'Fraud' : record.riskType == 'all' ? 'All' : 'Compliance';
          return (
            <div className={'flex items-center w-full'}>
              <CustomTableContentWrapper text={riskType} data-testid={`${ComponentName.ADMIN_USER}_riskType_text`} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.createdAt', defaultMessage: 'Created At' }),
        className: 'min-w-[10.5%]',
        body: (record: User) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper
                text={record.createdAt ? moment(record.createdAt).format('LLL') : ''}
                data-testid={`${ComponentName.ADMIN_USER}_createdAt_text`}
              />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'min-w-[10.5%]',
        body: (record: User) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper
                text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''}
                data-testid={`${ComponentName.ADMIN_USER}_updatedAt_text`}
              />
            </div>
          );
        },
      },
      {
        header: LanguageUtil.get({ id: 'pages.admin.user.editor.table.actions', defaultMessage: 'Actions' }),
        field: 'action',
        columnKey: 'action',
        hidden: this.environmentUtil.isLdapOnly(),
        sortable: false,
        body: (record: User) => {
          const {
            user: { username },
            checkerRequests,
          } = this.props;
          const blacklist = [NavigationUtil.getTenant(), 'trlogic', 'fraud_admin', 'compliance_admin', record.username];
          const request = checkerRequests.find((c) => c.documentId == record.username);
          if (request && request.type == 'DELETE') {
            return;
          }
          return (
            <div
              className={`flex w-full justify-start items-center ml-2 ${blacklist.includes(username) ? '!cursor-not-allowed' : ''}`}
              onClick={(e: MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                e.preventDefault();
                this.removeUser(e, record);
              }}
              data-testid={`${ComponentName.ADMIN_USER}_remove_button_${record.username}`}
            >
              <Tooltip position={'left'} target={'.remove'}>
                {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
              </Tooltip>
              <Icon
                icon={'delete'}
                className={`remove text-[#607080] ${username == record.username ? '!cursor-not-allowed' : 'hover:cursor-pointer'}`}
                size={20}
              />
            </div>
          );
        },
      },
    ];
    return columns as ColumnProps[];
  };

  private removeUser = (e: MouseEvent<HTMLDivElement>, record: User) => {
    const { onDeleteUser, user } = this.props;
    if (record.username == user.username) return;
    customConfirm({
      target: e.currentTarget,
      accept: () => {
        onDeleteUser(record.username);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deleteUser', defaultMessage: 'Do you sure delete this user?' }),
      acceptClassName: '!bg-primary !text-2xs',
    });
  };
}

interface Props extends ReduxProps {
  users: User[];
  selectedUser: User | undefined;
  checkerRequests: CheckerRequest[];
  updateRequestData: CheckerRequest<User> | undefined;
  setUpdateRequestData: (request: CheckerRequest | undefined) => void;
  openEditor: VoidFunction;
  setSelectedUser: (user: User) => void;
  onDeleteUser: (name: string) => void;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as UserState,
});
const mapDispatchToProps = { addPagePath, changePagePath };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(UserListComponent);
